import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import InvoicePrintCard from "../components/InvoicePrintCard";
import { InvoiceAPI } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Printer, FileDown, Trash2, Link2, Lock } from "lucide-react";

const InvoiceViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { unlocked } = useCenturion();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await InvoiceAPI.get(id);
        setInvoice(data);
      } catch {
        toast.error("Invoice not found");
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handlePrint = () => {
    document.title = `Invoice ${invoice?.invoice_number || ""} — ${invoice?.from_party?.company || ""}`;
    window.print();
  };

  const handleDelete = async () => {
    try {
      await InvoiceAPI.remove(id);
      toast.success("Invoice deleted");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyVerify = () => {
    const url = `${window.location.origin}/verify/${invoice.verification_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Verification URL copied");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-12 animate-pulse">
          <div className="h-6 w-40 bg-white/5 rounded mb-6" />
          <div className="bg-white/5 h-[600px] rounded-lg" />
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-[40px] font-semibold">Invoice not found</h2>
          <Link to="/" className="btn-primary mt-8 inline-flex">Back to dashboard</Link>
        </div>
      </Layout>
    );
  }

  const verificationUrl = `${window.location.origin}/verify/${invoice.verification_id}`;

  return (
    <Layout>
      <div className="no-print sticky top-[68px] z-20 bg-[#07060a]/80 backdrop-blur border-b border-[rgba(212,165,75,0.1)]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="text-[13px] text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <span className={`status-badge ${invoice.status}`}>{invoice.status}</span>
            <button onClick={copyVerify} className="btn-ghost"><Link2 size={14} /> Verify URL</button>
            <button onClick={handlePrint} className="btn-ghost"><Printer size={14} /> Print</button>
            <button onClick={handlePrint} className="btn-ghost"><FileDown size={14} /> PDF</button>
            {unlocked ? (
              <>
                <Link to={`/invoices/${invoice.id}/edit`} className="btn-ghost"><Pencil size={14} /> Edit</Link>
                <button onClick={() => setConfirmDelete(true)} className="btn-danger"><Trash2 size={14} /> Delete</button>
              </>
            ) : (
              <span className="text-[11px] text-white/45 flex items-center gap-1.5"><Lock size={12} /> Issuer locked</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <InvoicePrintCard invoice={invoice} verificationUrl={verificationUrl} />
      </div>

      {confirmDelete && (
        <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="glass max-w-md w-full mx-4 p-6">
            <h3 className="text-[18px] font-semibold mb-2">Delete invoice?</h3>
            <p className="text-white/60 text-[13px]">
              You're about to delete <span className="text-white font-mono">{invoice.invoice_number}</span>. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InvoiceViewPage;
